import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsappFAB from "@/components/WhatsappFAB";
import SEO from "@/components/SEO";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";

export default function Blog() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");

  useEffect(() => { api.get("/insights", { params: { published_only: true } }).then((r) => setItems(r.data)).catch(() => {}); }, []);

  const tags = useMemo(() => Array.from(new Set(items.flatMap((i) => i.tags || []))).slice(0, 12), [items]);
  const filtered = useMemo(() => items.filter((i) => {
    const okQ = !q || `${i.title} ${i.excerpt}`.toLowerCase().includes(q.toLowerCase());
    const okT = !tag || (i.tags || []).includes(tag);
    return okQ && okT;
  }), [items, q, tag]);
  const [featured, ...rest] = filtered;

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Blog" description="Field-tested playbooks on software, cloud, growth and product delivery — from the Maxine team." />
      <Nav />
      <main className="pt-28 md:pt-36 pb-20">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="eyebrow">Blog</div>
              <h1 className="font-display font-black text-5xl md:text-6xl leading-[1.02] tracking-tighter mt-4">
                Notes from the delivery floor.
              </h1>
            </div>
            <p className="lg:col-span-5 text-neutral-600 text-lg leading-relaxed">
              Field-tested playbooks on pricing, cloud residency, product ops and growth — written by the people who ship them.
            </p>
          </div>

          <div className="mb-10 flex flex-wrap gap-3 items-center border-y border-[color:var(--line)] py-4">
            <div className="flex items-center gap-2 border border-[color:var(--line)] px-3 py-2 flex-1 min-w-[220px] max-w-md focus-within:border-[color:var(--brand)]">
              <MagnifyingGlass size={14} className="text-neutral-500" />
              <input data-testid="blog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="flex-1 outline-none text-sm" />
            </div>
            <div className="flex flex-wrap gap-1">
              <button onClick={() => setTag("")} data-testid="blog-tag-all" className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${!tag ? "bg-[color:var(--brand)] text-white border-[color:var(--brand)]" : "border-[color:var(--line)] text-neutral-600 hover:border-[color:var(--brand)]"}`}>All</button>
              {tags.map((t) => (
                <button key={t} onClick={() => setTag(t)} data-testid={`blog-tag-${t}`} className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${tag === t ? "bg-[color:var(--brand)] text-white border-[color:var(--brand)]" : "border-[color:var(--line)] text-neutral-600 hover:border-[color:var(--brand)]"}`}>{t}</button>
              ))}
            </div>
          </div>

          {featured && (
            <Link to={`/insights/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} className="card-line block group mb-10 grid md:grid-cols-2">
              <div className="aspect-[16/10] md:aspect-auto md:min-h-[280px] border-r border-[color:var(--line)] bg-white grid-bg" />
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <div className="eyebrow">Featured</div>
                <h2 className="mt-3 font-display font-black text-3xl md:text-4xl leading-tight group-hover:text-[color:var(--brand)] transition-colors">{featured.title}</h2>
                <p className="mt-3 text-neutral-600">{featured.excerpt}</p>
                <div className="mt-6 font-mono text-xs uppercase tracking-widest text-[color:var(--brand)] flex items-center gap-2">
                  Read the story <ArrowRight size={14} weight="bold" />
                </div>
              </div>
            </Link>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p) => (
              <Link to={`/insights/${p.slug}`} key={p.id} data-testid={`blog-post-${p.slug}`} className="card-line p-6 flex flex-col group">
                <div className="aspect-[16/9] border border-[color:var(--line)] bg-white grid-bg" />
                <div className="mt-5 flex flex-wrap gap-2">
                  {(p.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="font-mono text-[10px] uppercase tracking-widest border border-[color:var(--line)] px-2 py-1 text-neutral-600">{t}</span>
                  ))}
                </div>
                <h3 className="mt-3 font-display font-bold text-xl leading-snug group-hover:text-[color:var(--brand)] transition-colors">{p.title}</h3>
                <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{p.excerpt}</p>
                <div className="mt-4 pt-4 border-t border-[color:var(--line)] font-mono text-[10px] uppercase tracking-widest text-neutral-500 flex justify-between">
                  <span>{p.author}</span>
                  <span>{String(p.created_at || "").slice(0, 10)}</span>
                </div>
              </Link>
            ))}
          </div>
          {filtered.length === 0 && <div className="p-12 text-center text-neutral-500">No posts match your filter.</div>}
        </div>
      </main>
      <Footer />
      <WhatsappFAB />
    </div>
  );
}
